<template>
    <div class="container admin-table-page">
        <!-- Fila del título y flecha hacia atrás -->
        <div class="param-title-wrap plan-cuentas-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- Fila de filtros debajo del título a la izquierda -->
        <div class="param-title-wrap plan-cuentas-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        id="report-select"
                        v-model="selReporte"
                        @change="iniciaPlan; fgl_consulta = false"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="reporte in listReporte" :key="reporte.id" :value="reporte.codigo">
                            {{ reporte?.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-form-select
                        id="validity-select"
                        v-model="selVigencia"
                        @change="iniciaPlan; fgl_consulta = false"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="vigencia in listVigencia" :key="vigencia.vigencia" :value="vigencia.vigencia">
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>
                    
                    <b-form-select
                        id="group-select"
                        v-model="grupoId"
                        @change="f_filterCompany"
                        class="filter-select ml-2"
                        :disabled="listGrupos.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectGroup }}
                        </b-form-select-option>
                        <option v-for="grupo in listGrupos" :key="grupo.id" :value="grupo.id">
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-form-select
                        id="company-select"
                        v-model="companyId"
                        @change="iniciaPlan; fgl_consulta = false"
                        class="filter-select ml-2"
                        :disabled="filteredCompany.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectCompany }}
                        </b-form-select-option>
                        <option v-for="company in filteredCompany" :value="company.id" :key="company.id">
                            {{ company?.acronym + " - " + company?.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-button
                        class="consult-btn ml-2"
                        @click="consultaCuentas(); fgl_consulta = true"
                        :disabled="!companyId || !selVigencia || !selReporte"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <!-- Spinner de carga -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- ESTADO INICIAL - Sin datos -->
            <div v-else-if="!fgl_consulta && listPlanCuentas.length === 0">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA CON DATOS -->
            <div v-else-if="fgl_consulta && listPlanCuentas.length > 0">
                <CorporateDataTable
                    :items="visibleItems"
                    :fields="allFields"
                    :filter-defs="tableFilterDefs"
                    :labels="tableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :toolbar-disabled="!fgl_consulta"
                >
                    <template #toolbar-actions>
                        <div class="plan-cuentas-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-secondary-btn"
                                @click="borraCriterio"
                                :disabled="!fgl_consulta"
                            >
                                <font-awesome-icon :icon="['fas', 'undo']" class="mr-1" />
                                {{ labels.clear }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="table-toolbar-secondary-btn ml-2"
                                @click="editVariasCuentas()"
                                :disabled="!fgl_consulta || isVigenciaBloqueada"
                            >
                                <font-awesome-icon :icon="['fas', 'pencil-alt']" class="mr-1" />
                                {{ labels.editMultiple }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(cuenta)="data">
                        <div class="text-center"><strong>{{ data.item?.cuenta }}</strong></div>
                    </template>

                    <template #cell(descripcion)="data">
                        <div class="text-left">{{ data.item?.descripcion }}</div>
                    </template>

                    <template #cell(nombre_niif)="data">
                        <div class="text-left">{{ data.item?.nombre_niif }}</div>
                    </template>

                    <template #cell(esf)="data">
                        <div class="text-center">
                            <b-badge :class="data.item?.esf == 'X' ? 'badge-indigo' : 'badge-light'" class="badge-lg">
                                {{ data.item?.esf == "X" ? "SI" : "NO" }}
                            </b-badge>
                        </div>
                    </template>

                    <template #cell(eri)="data">
                        <div class="text-center">
                            <b-badge :class="data.item?.eri == 'X' ? 'badge-indigo' : 'badge-light'" class="badge-lg">
                                {{ data.item?.eri == "X" ? "SI" : "NO" }}
                            </b-badge>
                        </div>
                    </template>

                    <template #cell(efe)="data">
                        <div class="text-center">
                            <b-badge :class="data.item?.efe == 'X' ? 'badge-indigo' : 'badge-light'" class="badge-lg">
                                {{ data.item?.efe == "X" ? "SI" : "NO" }}
                            </b-badge>
                        </div>
                    </template>

                    <template #cell(ecp)="data">
                        <div class="text-center">
                            <b-badge :class="data.item?.ecp == 'X' ? 'badge-indigo' : 'badge-light'" class="badge-lg">
                                {{ data.item?.ecp == "X" ? "SI" : "NO" }}
                            </b-badge>
                        </div>
                    </template>

                    <template #cell(interco)="data">
                        <div class="text-center">
                            <b-badge v-if="data.item?.intercoDescripcion" class="badge-indigo badge-lg">
                                {{ data.item?.intercoDescripcion }}
                            </b-badge>
                            <b-badge v-else class="badge-light badge-lg">
                                {{ labels.noGroup }}
                            </b-badge>
                        </div>
                    </template>

                    <template #cell(accionEdit)="data">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px;"
                            class="md-fab md-primary table-action-btn"
                            @click.stop="editCuenta(data.item.id)"
                            :disabled="!fgl_consulta || isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
                        </b-button>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA VACÍA - Consulta realizada sin resultados -->
            <div v-else-if="fgl_consulta && listPlanCuentas.length === 0" class="text-center py-5">
                <font-awesome-icon
                    :icon="['fas', 'table']"
                    size="3x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">
                    {{ labels.noData }}
                </p>
                <p class="text-muted">
                    {{ labels.noResults }}
                </p>
            </div>
        </div>

        <!-- Modales -->
        <Edit :showDialogEdit="showDialogEdit" @clicked="closeModal" @item-updated="updateItemInTable"
            :Item="itemSelected" :listNiif="listNiif" />
        <EditaVariasCuentas :showEditMasivo="showEditMasivo" :compVigSelect="selCompVig"
            :listadoCuentas="listPlanCuentas" :listadoNiif="listNiif" @reload="refrescaTabla" @clicked="closeModal" />

        <!-- Modal Confirmación Eliminar -->
        <InacticaItem 
            :InactivaItem="showDeleteModal" 
            @confirmDelete="confirmDelete" 
            @cierraModal="closeDeleteModal"
        />
    </div>
</template>

<script>
import Company from "../../services/Company" 
import PlanCuentas from "../../services/PlanCuentas" 
import PlanConsolidacion from '../../services/PlanConsolidacion'; 
import TipoReporte from '../../services/TipoReporte'; 
import Edit from "./Edit.vue"; 
import EditaVariasCuentas from "./EditaVariasCuentas.vue"; 
import Balance from "../../services/BalanceNiif" 
import CorporateDataTable from "../utils/CorporateDataTable.vue"
import InacticaItem from "../utils/InacticaItem.vue"

const toLower = (text) => {
    return text.toString()?.toLowerCase()
}

const searchByCuenta = (items, term) => {
    if (term) {
        return items?.filter((item) => toLower(item.cuenta).startsWith(term))
    }
    return items
}

const searchByNiif = (items, term) => {
    if (term) {
        const termLower = toLower(term)
        return items?.filter((item) => {
            if (item.niif == term) {
                return true
            }
            if (item.nombre_niif && toLower(item.nombre_niif).includes(termLower)) {
                return true
            }
            return false
        })
    }
    return items
}

export default {
    name: "PlanCuentas",
    components: {
        Edit,
        EditaVariasCuentas,
        CorporateDataTable,
        InacticaItem
    },
    data: () => ({
        fgl_consulta: false,
        searchCuenta: null,
        searchNiif: null,
        listPlanCuentas: [],
        listPlanCuentasOrigen: [],
        listCompany: [],
        companiaUsuario: [],
        listVigencia: [],
        companyId: "",
        selVigencia: "",
        showDialogEdit: false,
        showEditMasivo: false,
        showDeleteModal: false,
        itemSelected: {},
        listFirstLevel: [],
        listSecondLevel: [],
        listNiif: [],
        isLoading: false,
        selCompVig: {},
        selReporte: "",
        listReporte: [],
        grupoId: "",
        listGrupos: [],
        filteredCompany: [],
        itemLimit: 100,
        visibleItems: [],
        limitOptions: [
            { value: 100, text: '100 registros' },
            { value: 200, text: '200 registros' },
            { value: 500, text: '500 registros' },
            { value: 0, text: 'Mostrar todo' }
        ]
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Plan de Cuentas",
                    selectReport: "Seleccione Reporte",
                    selectValidity: "Seleccione Vigencia",
                    selectGroup: "Seleccione Grupo",
                    selectCompany: "Seleccione Compañía",
                    searchByCuenta: "Buscar por Cuenta",
                    searchByNiif: "Buscar por Niif",
                    account: "Cuenta",
                    description: "Descripción",
                    levelFirst: "Primer nivel",
                    levelSecond: "Segundo nivel",
                    niif: "Niif",
                    esf: "ESF",
                    eri: "ERI",
                    efe: "EFE",
                    ecp: "ECP",
                    interco: "Interco",
                    consultar: "Consultar",
                    edit: "Editar",
                    editMultiple: "Editar Múltiple",
                    noGroup: "NO",
                    noData: "No hay datos disponibles",
                    noResults: "No se encontraron resultados",
                    selectFilters: "Seleccione los filtros y consulte la información",
                    clear: "Limpiar",
                    noMatches: "No se encontraron resultados",
                    searchPlaceholder: "Buscar en la tabla...",
                    rowsPerPage: "Filas por página:",
                    noSort: "Sin orden",
                    sortAsc: "A-Z / Menor a mayor",
                    sortDesc: "Z-A / Mayor a menor",
                    columns: "Columnas",
                    showAllColumns: "Mostrar todas",
                    filters: "Filtros",
                    clearFilters: "Limpiar filtros",
                    filterAccount: "Filtrar cuenta...",
                    filterNiif: "Filtrar NIIF...",
                    loading: "Cargando..."
                }
            } else {
                return {
                    title: "Chart of Accounts",
                    selectReport: "Select Report",
                    selectValidity: "Select Validity",
                    selectGroup: "Select Group",
                    selectCompany: "Select Company",
                    searchByCuenta: "Search by account",
                    searchByNiif: "Search by Niif",
                    account: "Account",
                    description: "Description",
                    levelFirst: "Level first",
                    levelSecond: "Level second",
                    niif: "Niif",
                    esf: "ESF",
                    eri: "ERI",
                    efe: "EFE",
                    ecp: "ECP",
                    interco: "Interco",
                    consultar: "Consult",
                    edit: "Edit",
                    editMultiple: "Edit Multiple",
                    noGroup: "NO",
                    noData: "No data available",
                    noResults: "No results found",
                    selectFilters: "Select filters and consult the information",
                    clear: "Clear",
                    noMatches: "No results found",
                    searchPlaceholder: "Search in table...",
                    rowsPerPage: "Rows per page:",
                    noSort: "No sorting",
                    sortAsc: "A-Z / Low to high",
                    sortDesc: "Z-A / High to low",
                    columns: "Columns",
                    showAllColumns: "Show all",
                    filters: "Filters",
                    clearFilters: "Clear filters",
                    filterAccount: "Filter account...",
                    filterNiif: "Filter NIIF...",
                    loading: "Loading..."
                }
            }
        },
        allFields() {
            return [
                { key: 'cuenta', label: this.labels.account, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.description, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'nombre_niif', label: this.labels.niif, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'esf', label: this.labels.esf, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'eri', label: this.labels.eri, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'efe', label: this.labels.efe, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'ecp', label: this.labels.ecp, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'interco', label: this.labels.interco, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'cuenta', type: 'text', placeholder: this.labels.filterAccount },
                { key: 'nombre_niif', type: 'text', placeholder: this.labels.filterNiif }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        }
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        
        iniciaPlan() {
            this.listPlanCuentas = []
            this.visibleItems = []
            this.fgl_consulta = false
        },
        
        consultaCuentas() {
            if (!this.companyId || !this.selVigencia || !this.selReporte) {
                this.$toast.error('Seleccione todos los filtros requeridos.')
                return
            }
            this.isLoading = true
            
            Promise.all([
                PlanCuentas.getCuentasPorCompaVigRepor(this.companyId, this.selVigencia, this.selReporte),
                PlanConsolidacion.getPlanConsPorCompVige(this.selVigencia, this.selReporte)
            ])
                .then(([resCuentas, resNiif]) => {
                    this.listPlanCuentas = resCuentas.data.planCuentas.map(cuenta => ({
                        ...cuenta,
                        intercoDescripcion: cuenta.interco_descripcion
                    }))
                    this.listPlanCuentasOrigen = [...this.listPlanCuentas]
                    this.listNiif = resNiif.data.planConsNiif
                    this.resetVisibleItems()
                    this.fgl_consulta = true
                })
                .catch((err) => {
                    console.log(err)
                    this.$toast.error('Error al consultar los datos')
                    this.fgl_consulta = true
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        
        editCuenta(id) {
            if (!this.fgl_consulta || this.isVigenciaBloqueada) {
                if (this.isVigenciaBloqueada) {
                    this.$toast.error('La vigencia seleccionada está deshabilitada para edición.')
                }
                return
            }
            const item = this.listPlanCuentas.find((r_plan) => r_plan.id == id)
            this.itemSelected = item
            this.showDialogEdit = true
        },
        
        editVariasCuentas() {
            if (!this.fgl_consulta) return
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para edición.')
                return
            }
            this.selCompVig.companyId = this.companyId
            this.selCompVig.vigencia = this.selVigencia
            this.selCompVig.codReporte = this.selReporte
            this.showEditMasivo = true
        },
        
        closeModal(value) {
            this.showDialogEdit = value
            this.showEditMasivo = value
        },
        
        closeDeleteModal() {
            this.showDeleteModal = false
        },
        
        confirmDelete() {
            this.showDeleteModal = false
        },
        
        refrescaTabla(vigencia, idCompany, codReporte) {
            this.isLoading = true
            PlanCuentas.getCuentasPorCompaVigRepor(idCompany, vigencia, codReporte)
                .then((res) => {
                    this.listPlanCuentas = res.data.planCuentas.map(cuenta => ({
                        ...cuenta,
                        intercoDescripcion: cuenta.interco_descripcion
                    }))
                    this.resetVisibleItems()
                })
                .catch((err) => {
                    console.log(err)
                })
                .finally(() => {
                    this.isLoading = false
                    this.showDialogEdit = false
                    this.showEditMasivo = false
                })
        },
        
        borraCriterio() {
            if (!this.fgl_consulta) return
            this.searchCuenta = ''
            this.searchNiif = ''
            this.listPlanCuentas = this.listPlanCuentasOrigen
            this.resetVisibleItems()
        },
        
        searchOnTableAcount() {
            this.listPlanCuentas = searchByCuenta(this.listPlanCuentasOrigen, this.searchCuenta)
            if (this.searchCuenta == '') {
                this.listPlanCuentas = this.listPlanCuentasOrigen
            }
            this.resetVisibleItems()
        },
        
        searchOnTableNiif() {
            this.listPlanCuentas = searchByNiif(this.listPlanCuentasOrigen, this.searchNiif)
            if (this.searchNiif == '') {
                this.listPlanCuentas = this.listPlanCuentasOrigen
            }
            this.resetVisibleItems()
        },
        
        resetVisibleItems() {
            if (this.itemLimit === 0) {
                this.visibleItems = [...this.listPlanCuentas]
            } else {
                this.visibleItems = this.listPlanCuentas.slice(0, this.itemLimit)
            }
        },
        
        f_filterCompany() {
            if (this.grupoId) {
                const grupoIdNum = this.grupoId.toString()
                this.filteredCompany = this.listCompany.filter(company => {
                    if (!company?.id_grupos) return false
                    const gruposCompania = company.id_grupos.split(',').map(id => id.trim())
                    return gruposCompania?.includes(grupoIdNum)
                })
            } else {
                this.filteredCompany = this.listCompany
            }
            this.companyId = ""
            this.fgl_consulta = false
        },
        
        updateItemInTable(updatedItem) {
            const index = this.listPlanCuentas.findIndex(item => item.id === updatedItem.id)
            if (index !== -1) {
                this.$set(this.listPlanCuentas, index, updatedItem)
                const indexOrigen = this.listPlanCuentasOrigen.findIndex(item => item.id === updatedItem.id)
                if (indexOrigen !== -1) {
                    this.$set(this.listPlanCuentasOrigen, indexOrigen, updatedItem)
                }
                this.resetVisibleItems()
            }
        },
        
        async loadUserCompanies() {
            try {
                const userResult = await Balance.leaCompaniaUsuario()
                this.companiaUsuario = userResult.data.companiaUsuario
                const companiesResult = await Company.getAllCompanies()
                const allCompanies = companiesResult.data.companies
                const filteredCompanies = allCompanies.filter((company) => {
                    if (this.$store.state.userRole == 1) {
                        return true
                    }
                    const userHasAccess = this.companiaUsuario.find(
                        (userCompany) =>
                            userCompany.id_usuario == this.$store.state.userId &&
                            userCompany.id_compania == company.id
                    )
                    return userHasAccess
                })
                this.listCompany = filteredCompanies
                this.filteredCompany = filteredCompanies
            } catch (err) {
                console.error("Error loading companies:", err)
                throw err
            }
        },
        
        async loadVigencias() {
            try {
                const result = await PlanCuentas.getVigenciaPlancuentas()
                this.listVigencia = result.data.getVigencia
            } catch (err) {
                console.error("Error loading vigencias:", err)
                throw err
            }
        },
        
        async loadReportTypes() {
            try {
                const res = await TipoReporte.getAllTipoReporte()
                this.listReporte = res.data.typeReport
            } catch (err) {
                console.error("Error loading report types:", err)
                throw err
            }
        },
        
        async loadGrupos() {
            try {
                const Grupo = await import("../../services/Grupo")
                const result = await Grupo.default.getAllGrupos()
                this.listGrupos = result.data.grupos
            } catch (err) {
                console.error("Error loading grupos:", err)
            }
        }
    },
    mounted() {
        this.isLoading = true
        
        Promise.all([
            this.loadUserCompanies(),
            this.loadVigencias(),
            this.loadReportTypes(),
            this.loadGrupos()
        ])
        .catch((err) => {
            console.error("Error loading data:", err)
        })
        .finally(() => {
            this.isLoading = false
        })
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* Estilos para el contenedor de filtros - ahora a la izquierda */
.plan-cuentas-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.plan-cuentas-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

/* Para que el texto de las opciones se vea completo */
.plan-cuentas-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

/* Estilo para selects deshabilitados */
.plan-cuentas-header .filter-select:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #e9ecef;
}

.plan-cuentas-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.plan-cuentas-header .consult-btn:hover,
.plan-cuentas-header .consult-btn:focus,
.plan-cuentas-header .consult-btn:active,
.plan-cuentas-header .consult-btn:not(:disabled):hover,
.plan-cuentas-header .consult-btn:not(:disabled):focus,
.plan-cuentas-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.plan-cuentas-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

/* Estilos para la flecha y título */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* Toolbar actions */
.plan-cuentas-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-secondary-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #6c757d;
    color: white;
    border: 1px solid #6c757d;
}

.table-toolbar-secondary-btn:hover:not(:disabled) {
    background-color: #5a6268;
    border-color: #545b62;
    color: white;
}

.table-toolbar-secondary-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d;
    border-color: #6c757d;
    color: white;
}

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

/* Ajustes responsivos */
@media (max-width: 1200px) {
    .plan-cuentas-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    
    .plan-cuentas-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .plan-cuentas-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    
    .plan-cuentas-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    
    .plan-cuentas-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .plan-cuentas-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    
    .plan-cuentas-header .filters-container {
        gap: 4px;
    }
    
    .plan-cuentas-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }
}
</style>